import type { ExportHeader } from '@/types/export.types';

/**
 * Fixed headers for case record columns
 */
export const CASE_HEADERS: ExportHeader[] = [
  { name: 'id', label: 'ID', type: 'string' },
  { name: 'reference_id', label: 'Reference ID', type: 'string' },
  { name: 'state', label: 'State', type: 'string' },
  { name: 'assigned', label: 'Assigned', type: 'string' },
  { name: 'created_by', label: 'Created By', type: 'string' },
  { name: 'created_at', label: 'Created At', type: 'date' },
  { name: 'workflow_completed_at', label: 'Completed At', type: 'date' },
];

/**
 * SLA columns (BV-1846) - only included for the payor workflow types listed in
 * DISPOSITION_WORKFLOW_TYPES. Values mirror the case-list SLA timer via
 * `computeSlaValues`, so "SLA Timer" holds exactly the time the table's
 * timer shows and "SLA Flag" holds its badge text (blank when there is no badge).
 */
export const SLA_HEADERS: ExportHeader[] = [
  { name: 'sla_flag', label: 'SLA Flag', type: 'string' },
  { name: 'sla_timer', label: 'SLA Timer', type: 'string' },
];

/**
 * Disposition header - only included for workflows that support case disposition
 * (PBM, UNIFIEDMEDBV). Must stay in sync with TableBuilder's getStandardColumns.
 */
export const DISPOSITION_HEADER: ExportHeader = {
  name: 'disposition',
  label: 'Disposition',
  type: 'string',
};

/**
 * Fixed headers for call record columns
 */
export const CALL_HEADERS: ExportHeader[] = [
  { name: 'call_sid', label: 'Call SID', type: 'string' },
  { name: 'agent_persona', label: 'Agent Persona', type: 'string' },
  { name: 'call_created_at', label: 'Call Created At', type: 'date' },
  { name: 'call_duration', label: 'Call Duration', type: 'string' },
  { name: 'call_type', label: 'Call Type', type: 'string' },
  { name: 'call_status', label: 'Call Status', type: 'string' },
  { name: 'call_disposition', label: 'Call Disposition', type: 'string' },
  { name: 'call_disposition_notes', label: 'Call Disposition Notes', type: 'string' },
  { name: 'started_at', label: 'Call Started At', type: 'date' },
  { name: 'chart_info', label: 'Reachout Note', type: 'string' },
  { name: 'phone', label: 'Phone Called At', type: 'string' },
];

/**
 * Fixed headers for eligibility check columns
 */
export const ELIGIBILITY_CHECK_HEADERS: ExportHeader[] = [
  { name: 'eligibility_check_id', label: 'Eligibility Check ID', type: 'string' },
  { name: 'eligibility_check_status', label: 'Eligibility Check Status', type: 'string' },
  { name: 'eligibility_check_created_at', label: 'Eligibility Check Created At', type: 'date' },
  { name: 'eligibility_check_updated_at', label: 'Eligibility Check Updated At', type: 'date' },
  { name: 'eligibility_check_payer_id', label: 'Payer ID', type: 'string' },
  { name: 'eligibility_check_attempt', label: 'Attempt', type: 'number' },
  { name: 'eligibility_check_errors', label: 'Errors', type: 'string' },
  { name: 'eligibility_check_case_state', label: 'Case State', type: 'string' },
];

/**
 * Default sheet name for Excel exports
 */
export const DEFAULT_SHEET_NAME = 'Case Records';

/**
 * Default CSV delimiter
 */
export const CSV_DELIMITER = ',';

/**
 * Export batch size for API calls
 */
export const EXPORT_BATCH_SIZE = 10;

/**
 * Page size for fetching case records
 */
export const EXPORT_PAGE_SIZE = 100;

/**
 * Safety cap on the number of case records pulled into the browser for a single
 * export. The org-wide (Tier-2) export paginates client-side, so this bounds
 * memory/time; beyond it the user is asked to narrow filters.
 */
export const MAX_EXPORT_RECORDS = 5000;

/**
 * Excel maximum cell character length
 * Excel cells have a hard limit of 32,767 characters
 */
export const EXCEL_MAX_CELL_LENGTH = 32767;
